import * as React from 'react';
import {applyContainerQuery} from 'react-container-query';
import {connect} from 'react-redux';

import classnames from 'classnames';
import type Moment from 'moment';

import {applyFilterSubmission} from 'nutshell-core';

import {convertToObj} from 'nutshell-core/filter';
import type {ReportFacet, FilterObject, ComparisonRangeType} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {FilterButton} from 'shells/filter-button/filter-button';
import {Select} from 'shells/select/select';

import type {NutshellState} from '../../store';

import {ActivityReportFilters} from './activity/activity-report-filters';
import {EmailReportFilters} from './activity/email-report-filters';
import {FacetPicker} from './facet-picker';
import {ConfidenceToggle} from './forecast/confidence-toggle';
import {
    ACTIVITY,
    EMAIL,
    TASKS,
    CUSTOM,
    ENGAGEMENT,
    getReportDateRanges,
    getReportFieldsFilterFunction,
    getReportSegments,
    CHATBOT,
} from './report-constants';
import {ReportDateRangePicker} from './report-date-range-picker';
import type {ReportKey, DateRangeType} from './report-types';
import * as actions from './reports-actions';
import * as reportsSelectors from './reports-selectors';
import {TaskReportFilters} from './tasks/task-report-filters';

import styles from './report-controls.module.css';

type OwnProps = {
    reportKey: ReportKey;
    dateRangeType: DateRangeType;
    facet?: ReportFacet;
    segment?: string;
    activeFilters: Array<FilterObject>;
    hasConfidenceToggle?: boolean;
    useConfidence?: boolean;
    comparisonType?: ComparisonRangeType;
    // @ts-expect-error
    minDate?: Moment;
    // @ts-expect-error
    maxDate?: Moment;
    onFacetChange: (...args: any) => any;
    onFilterChange: (...args: any) => any;
    onDateRangeChange: (arg1: FilterObject, comp?: ComparisonRangeType) => any;
    onSegmentChange?: (...args: any) => any;
    onUseConfidenceChange?: (...args: any) => any;
    onRemoveAllFilters: (...args: any) => any;
    noPadding?: boolean;
};
type StateProps = {
    isFiltering: boolean;
};
type DispatchProps = {
    onIsFilteringChange: (...args: any) => any;
};
type ContainerQueryProps = {
    containerQuery: any;
};
type Props = OwnProps & ContainerQueryProps & StateProps & DispatchProps;

function mapStateToProps(state: NutshellState): StateProps {
    return {
        isFiltering: reportsSelectors.getIsFilterDrawerOpen(state),
    };
}

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        onIsFilteringChange: () => {
            dispatch(actions.toggleShowFilterDrawer());
        },
    };
}

// We want to change the label copy of the confidence toggle on smaller screens
// and this is our cutoff
const query = {
    'confidence-small': {
        maxWidth: 1100,
    },
    'hide-remove-button': {
        maxWidth: 900,
    },
} as const;

class ReportControlsComponent extends React.PureComponent<Props> {
    render() {
        const allowsDateComparison =
            this.props.reportKey !== TASKS && this.props.reportKey !== CHATBOT;
        const fieldFilterFunction =
            this.props.reportKey === CUSTOM
                ? // @ts-expect-error
                  (fieldName) => ![this.props.dateRangeType].includes(fieldName)
                : getReportFieldsFilterFunction(this.props.reportKey);
        // @ts-expect-error
        const filterObjFilter = (filterObj) => fieldFilterFunction(Object.keys(filterObj)[0]);
        const activeFilters = this.props.activeFilters.filter(filterObjFilter);
        const segmentOptions = getReportSegments(this.props.reportKey);
        let filteredSegmentOptions = segmentOptions;

        if (!NutClientConfig.hasTerritories) {
            filteredSegmentOptions = filteredSegmentOptions.filter(
                (option) => option.id !== 'territories'
            );
        }

        return (
            // Additional div here is necessary for the container query
            // to hook into. Otherwise, it places a DOM node on the container,
            // which messes up our beautiful `justify-content: space-between`
            <div>
                <div
                    className={classnames(styles.container, {
                        [styles['container--no-padding']]: this.props.noPadding,
                    })}
                >
                    <div className={styles['left-controls']}>
                        {this.props.reportKey !== ACTIVITY &&
                        this.props.reportKey !== EMAIL &&
                        this.props.reportKey !== TASKS &&
                        this.props.reportKey !== ENGAGEMENT &&
                        this.props.reportKey !== CHATBOT ? (
                            <FilterButton
                                isOpen={this.props.isFiltering}
                                onClick={this.props.onIsFilteringChange}
                                activeFilters={activeFilters}
                            />
                        ) : undefined}
                        {this.props.segment ? (
                            <Select
                                // @ts-expect-error
                                size='large'
                                value={filteredSegmentOptions.find(
                                    (opt) => opt.id === this.props.segment
                                )}
                                prefix='Segment by: '
                                labelKey='label'
                                selectValues={filteredSegmentOptions}
                                styleOptions={{filled: true, color: 'blue'}}
                                onChange={this.props.onSegmentChange}
                            />
                        ) : undefined}
                        {this.props.hasConfidenceToggle &&
                        typeof this.props.useConfidence !== 'undefined' &&
                        this.props.onUseConfidenceChange ? (
                            <ConfidenceToggle
                                useConfidence={this.props.useConfidence}
                                isDisabled={this.props.facet === 'count'}
                                isSmall={this.props.containerQuery['confidence-small']}
                                onToggle={this.props.onUseConfidenceChange}
                            />
                        ) : undefined}
                        {this.props.facet &&
                        this.props.reportKey !== ACTIVITY &&
                        this.props.reportKey !== EMAIL &&
                        this.props.reportKey !== TASKS ? (
                            <div className={styles['facet-picker-container']}>
                                <FacetPicker
                                    // @ts-expect-error
                                    name={`${this.props.reportKey}FacetPicker`}
                                    facet={this.props.facet}
                                    onFacetChange={this.props.onFacetChange}
                                />
                            </div>
                        ) : undefined}
                        {this.props.reportKey === ACTIVITY ? (
                            <ActivityReportFilters
                                onFilterSubmit={this.handleFilterSubmit}
                                onFilterRemove={this.handleFilterRemove}
                                filters={this.props.activeFilters}
                            />
                        ) : undefined}
                        {this.props.reportKey === EMAIL ? (
                            <EmailReportFilters
                                onFilterSubmit={this.handleFilterSubmit}
                                onFilterRemove={this.handleFilterRemove}
                                filters={this.props.activeFilters}
                            />
                        ) : undefined}
                        {this.props.reportKey === TASKS && (
                            <TaskReportFilters
                                filters={this.props.activeFilters}
                                onFilterSubmit={this.handleFilterSubmit}
                                onFilterRemove={this.handleFilterRemove}
                            />
                        )}
                        {this.props.reportKey !== ACTIVITY &&
                        this.props.reportKey !== EMAIL &&
                        this.props.reportKey !== TASKS &&
                        this.props.reportKey !== ENGAGEMENT &&
                        this.props.reportKey !== CHATBOT &&
                        activeFilters.length ? (
                            <div
                                className={
                                    this.props.containerQuery['hide-remove-button']
                                        ? styles['remove-button-container__hidden']
                                        : styles['remove-button-container']
                                }
                            >
                                <Button
                                    size='small'
                                    variant='text-primary'
                                    onClick={this.props.onRemoveAllFilters}
                                >
                                    Remove filters
                                </Button>
                            </div>
                        ) : undefined}
                    </div>
                    <ReportDateRangePicker
                        activeFilters={this.props.activeFilters}
                        dateRangeType={this.props.dateRangeType}
                        // @ts-expect-error
                        dateRangeOptions={getReportDateRanges(this.props.reportKey)}
                        comparisonType={
                            allowsDateComparison ? this.props.comparisonType : undefined
                        }
                        onDateRangeChange={this.props.onDateRangeChange}
                        hideCompareTo={!allowsDateComparison}
                        minDate={this.props.minDate}
                        maxDate={this.props.maxDate}
                    />
                </div>
            </div>
        );
    }

    /**
     * Takes a filterObj and either updates the existing version of that
     * filter or tacks it on as a new filter to our filters state.
     * @param  {object} filterObj  Filter object to update/apply
     * @return {void}
     */
    handleFilterSubmit = (filterObj: any) => {
        const newFilters = applyFilterSubmission(this.props.activeFilters, filterObj);
        this.props.onFilterChange(convertToObj(newFilters));
    };

    /**
     * Takes a filterKey (read: `owner`, `confidence`) and removes that
     * filter from the current set of filters.
     *
     * @param  {string} filterKey name of filter to remove
     * @return {void}
     */
    handleFilterRemove = (filterKey: string) => {
        const newFilters = this.props.activeFilters.filter(
            (filterObj) => !Object.prototype.hasOwnProperty.call(filterObj, filterKey)
        );
        this.props.onFilterChange(convertToObj(newFilters));
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps & ContainerQueryProps, _, _, _, _>(
    mapStateToProps,
    mapDispatchToProps
);

export const ReportControls = applyContainerQuery(connector(ReportControlsComponent), query);
